import { useCallback } from "react";
import notificationService from "../services/notification-service.js";
import { useNotificationsStore } from "../stores/use-notifications-store.js";
import { useNotificationsMountedGuard } from "./use-notifications-mounted-guard.js";

/**
 * Marca uma notificação como lida (otimista + rollback em falha).
 * `isVirtual`: só atualiza estado local — sem POST na API.
 * Requer NotificationsProvider (guarda de escopo).
 *
 * @returns {{
 *   markAsRead: (notificationId: string, requestOptions?: RequestInit) => Promise<*>,
 *   isLoading: boolean,
 *   error: string|null,
 * }}
 */
export function useMarkNotificationAsRead() {
  useNotificationsMountedGuard();

  const isLoading = useNotificationsStore((state) => state.isMarkingRead);
  const error = useNotificationsStore((state) => state.error);
  const applyMarkAsReadOptimistic = useNotificationsStore(
    (state) => state.applyMarkAsReadOptimistic,
  );
  const revertNotification = useNotificationsStore(
    (state) => state.revertNotification,
  );
  const finishMarkAsRead = useNotificationsStore(
    (state) => state.finishMarkAsRead,
  );
  const clearError = useNotificationsStore((state) => state.clearError);

  const markAsRead = useCallback(
    async (notificationId, requestOptions) => {
      const id =
        typeof notificationId === "string" ? notificationId.trim() : "";
      if (!id) {
        clearError("notificationId inválido");
        return {
          success: false,
          message: "notificationId inválido",
          status: 400,
          code: "INVALID_NOTIFICATION_ID",
        };
      }

      const current = useNotificationsStore
        .getState()
        .notifications.find((item) => item?.id === id);
      const isVirtual = Boolean(current?.isVirtual);

      const snapshot = applyMarkAsReadOptimistic(id);
      if (!snapshot) {
        clearError("Notificação não encontrada");
        return {
          success: false,
          message: "Notificação não encontrada",
          status: 404,
          code: "NOTIFICATION_NOT_FOUND",
        };
      }

      if (isVirtual) {
        finishMarkAsRead();
        clearError(null);
        return {
          success: true,
          message: "Notificação virtual marcada como lida",
          status: 200,
          data: { status: "read", virtual: true },
        };
      }

      try {
        const response = await notificationService.markAsRead(
          id,
          requestOptions,
        );

        if (!response?.success) {
          revertNotification(id, snapshot);
          clearError(
            response?.message || "Erro ao marcar notificação como lida",
          );
          return response;
        }

        finishMarkAsRead();
        clearError(null);
        return response;
      } catch (err) {
        revertNotification(id, snapshot);
        const message =
          err?.message || "Erro ao marcar notificação como lida";
        clearError(message);
        return {
          success: false,
          message,
          status: 500,
          code: "UNKNOWN_ERROR",
          error: err,
        };
      }
    },
    [
      applyMarkAsReadOptimistic,
      revertNotification,
      finishMarkAsRead,
      clearError,
    ],
  );

  return {
    markAsRead,
    isLoading,
    error,
  };
}
